import { useT } from "@transifex/react";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import DesignConstants from "../../constants/DesignConstants";
import { OnboardingProgressBar } from "./onboardingProgressBar/OnboardingProgressBar";
import { OnboardingStepItem } from "./onboardingStepItem/OnboardingStepItem";

import { CreationModes } from "../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useTestCreationContext } from "../../context/TestCreationContext";
import { useLogger } from "../../hooks/useLogger";
import { getCurrentDateTimeFormatted } from "../../helpers/DateTime";
import { cp } from "../../config";
import { personalDataWebsitePath } from "../../constants/RedirectConstants";
import {
    ChevronIcon,
    ExpandableHeader,
    ExpandableHeaderContainer,
    OnboardingCollapsibleSection,
    OnboardingProgressWrapper,
    OnboardingStepListItem,
    OnboardingStepsContainer,
} from "./StyledComponents";

export interface OnboardingStep {
    title: string;
    description?: string;
    href: string;
    completed: boolean;
}

interface OnboardingProps {
    hasCreatedTest: boolean;
    hasCompletedProfile: boolean;
    hasVocabTraining: boolean;
}

export const Onboarding = (props: OnboardingProps) => {
    const [isExpanded, setIsExpanded] = useState(() => {
        const status = localStorage.getItem("IS_ONBOARDING_EXPANDED");
        return status === null ? true : JSON.parse(status);
    });

    const { setCreatingMode } = useGeneralContext();
    const { setTestName, setSelectedTitleTag } = useTestCreationContext();
    const { logEvent } = useLogger();
    const history = useHistory();
    const t = useT();

    const createFirstTest = t("Create your first test", { _tags: "AssistantOverview" });
    const completeProfile = t("Complete your profile", { _tags: "AssistantOverview" });
    const unlockUnlimitedTests = t("And unlock unlimited tests.", { _tags: "AssistantOverview" });
    const createVocabularyTraining = t("Create your first vocabulary training", { _tags: "AssistantOverview" });

    const meetOurAssistants = t("Meet our digital assistant.", { _tags: "AssistantOverview" });
    const startUsingOurAssistant = t("Start using our assistant quickly with these simple steps", {
        _tags: "AssistantOverview,ScreenReaderOnly",
    });

    const onboardingStepsExpanded = t("Steps are expanded", { _tags: "AssistantOverview" });
    const onboardingStepsCollapsed = t("Steps are collapsed", { _tags: "AssistantOverview" });
    const t_defaultTestName = t("Test from {currentDate}", {
        currentDate: getCurrentDateTimeFormatted(),
        _tags: "CreateTest,CreateHome",
    });

    const onboardingSteps: OnboardingStep[] = [
        {
            title: createFirstTest,
            href: "/create/choose-source",
            completed: props.hasCreatedTest,
        },
        {
            title: completeProfile,
            description: unlockUnlimitedTests,
            href: `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}${personalDataWebsitePath}`,
            completed: props.hasCompletedProfile,
        },
        {
            title: createVocabularyTraining,
            href: "/create/choose-vocab-training-source",
            completed: props.hasVocabTraining,
        },
    ];

    useEffect(() => {
        localStorage.setItem("IS_ONBOARDING_EXPANDED", JSON.stringify(isExpanded));
    }, [isExpanded]);

    const calcOnboardingProgress = () => {
        const completedSteps = onboardingSteps.filter((step) => step.completed).length;
        return (completedSteps / onboardingSteps.length) * 100;
    };

    return (
        <OnboardingProgressWrapper aria-label={startUsingOurAssistant}>
            <ExpandableHeaderContainer
                aria-controls="onboarding-content"
                aria-label={isExpanded ? onboardingStepsExpanded : onboardingStepsCollapsed}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <ExpandableHeader id="checklist-title">{meetOurAssistants}</ExpandableHeader>
                <ChevronIcon isExpanded={isExpanded}>
                    <PhaseSixIcon
                        name="chevron-right"
                        style={{ fontSize: DesignConstants.FONT_SIZES.XL, color: DesignConstants.COLORS.DARK_GREY }}
                    />
                </ChevronIcon>
            </ExpandableHeaderContainer>
            {isExpanded && (
                <OnboardingCollapsibleSection id="onboarding-content">
                    <OnboardingProgressBar progress={calcOnboardingProgress()} />
                    <OnboardingStepsContainer
                        aria-labelledby="checklist-title"
                        tabIndex={0}
                    >
                        {onboardingSteps.map((step, idx) => (
                            <OnboardingStepListItem key={`step-list-item-${idx}`}>
                                <OnboardingStepItem
                                    step={step}
                                    onStepClick={() => {
                                        if (step.href.startsWith("https")) {
                                            window.open(step.href);
                                        } else if (step.href === "/create/choose-source") {
                                            logEvent("Test Creation Started", {
                                                entry_point: "onboarding",
                                                creating_mode: CreationModes.TEST,
                                            });
                                            setCreatingMode(CreationModes.TEST);
                                            setTestName(t_defaultTestName);
                                            history.push(step.href);
                                        } else if (step.href === "/create/choose-vocab-training-source") {
                                            setCreatingMode(CreationModes.RECURRING_TASK);
                                            setTestName("");
                                            setSelectedTitleTag(null);
                                            history.push(step.href);
                                        } else {
                                            history.push(step.href);
                                        }
                                    }}
                                />
                            </OnboardingStepListItem>
                        ))}
                    </OnboardingStepsContainer>
                </OnboardingCollapsibleSection>
            )}
        </OnboardingProgressWrapper>
    );
};
